import { Alert, Box, Button } from '@mui/material'
import { useAPI } from 'contexts/APIProvider'
import { useJobEmployment } from 'contexts/JobEmploymentProvider'
import { useNurse } from 'contexts/NurseProvider'
import { usePractice } from 'contexts/PracticeProvider'
import React from 'react'
import { useNavigate } from 'react-router'
import { TimeSheetChangeForm } from 'types/interfaces'
import update from 'immutability-helper'
import TimeSheetChangeError from 'errors/TimeSheetChangeError'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import { isEmpty } from 'lodash'
import { practiceViewTimeSheetURL, nurseViewTimeSheetURL } from 'routes/urls'
import Header from 'components/Header'
import FormField from 'components/FormField'
import ErrorList from 'components/ErrorList'

const TimeSheetProposeChange: React.FC = () => {
  const navigate = useNavigate()
  const { showAlert } = useSnackBarAlert()
  const { je } = useJobEmployment()
  const { practice } = usePractice()
  const { nurse } = useNurse()
  const { api } = useAPI()
  const [form, setForm] = React.useState<TimeSheetChangeForm>({})
  const [error, setError] = React.useState<TimeSheetChangeError>();
  const [loading, setLoading] = React.useState<boolean>(false)

  const usertype = React.useMemo(() => {
    if (practice) return 'practice'
    if (nurse) return 'nurse'
    return null
  }, [nurse, practice])

  const successURL = React.useMemo(() => {
    if (!je) return null
    if (practice) return practiceViewTimeSheetURL(practice.practice_id, je.id)
    if (nurse) return nurseViewTimeSheetURL(nurse.nurse_id, je.id)
    return null
  }, [je, nurse, practice])

  function updateForm(name: string, value: any) {
    setForm(update(form, { [name]: { $set: value } }))
    if (error) setError(update(error, { [name]: { $set: [] } }))
  }

  async function save() {
    if (!successURL) return
    try {
      setLoading(true)
      await api.createTimeSheetChange(form)
      showAlert('success', 'TimeSheet change proposed')
      navigate(successURL)
    } catch (e) {
      if (e instanceof TimeSheetChangeError) {
        setError(e)
      } else {
        console.error('unhandled exception', e)
      }
    } finally {
      setLoading(false)
    }
  }

  async function prepopulateForm() {
    if (!je) return
    if (!usertype) return
    if (!isEmpty(form)) return
    const tz = usertype === 'practice' ? je.practice_tz : je.nurse_tz
    setForm((form) => update(form, {
      job_employment_id: { $set: je.id },
      created_by: { $set: usertype },
      new_start_time: { $set: je.start_at.setZone(tz).toFormat('HH:mm') },
      new_end_time: { $set: je.end_at.setZone(tz).toFormat('HH:mm') },
      new_lunch_break: { $set: je.lunch_break.as('minute') },
    }))
  }


  React.useEffect(() => {
    prepopulateForm()
  }, [je, usertype])

  if (isEmpty(form)) {
    return null
  }
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
      <Header variant='h1' text='Propose timesheet change'></Header>
      <FormField
        name='new_start_time'
        label='Start Time'
        type='time'
        onChange={(e) => updateForm('new_start_time', e.target.value)}
        value={form.new_start_time}
        errors={error?.new_start_time}
      />
      <FormField
        name='new_end_time'
        label='End Time'
        type='time'
        onChange={(e) => updateForm('new_end_time', e.target.value)}
        value={form.new_end_time}
        errors={error?.new_end_time}
      />
      <FormField
        name='new_lunch_break'
        label='Lunch Break (minutes)'
        type='number'
        onChange={(e) => updateForm('new_lunch_break', e.target.value)}
        value={form.new_lunch_break}
        errors={error?.new_lunch_break}
      />
      <FormField
        name='comments'
        label='Reason'
        multiline
        rows={4}
        onChange={(e) => updateForm('comments', e.target.value)}
        value={form.comments}
        errors={error?.comments}
      />
      {error?.schema ? (
        <Alert severity='error' variant='filled'><ErrorList errors={error.schema} /></Alert>
      ) : null}
      <Button variant="contained" color="primary" onClick={save} disabled={loading}>
        {loading ? 'Please Wait ...' : 'Submit'}
      </Button>
      <Button
        variant="outlined" color="primary" disabled={loading}
        onClick={() => successURL ? navigate(successURL) : null}>
        Cancel
      </Button>
    </Box>
  )
}

export default TimeSheetProposeChange